import { useLocation, useNavigate, useParams } from 'react-router';
import { useCaseRecord } from '@/application/usecases/useArchive';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { ArchiveIcon, GoBackIcon } from '@/ui/icons';
import { PageLoading } from '@/ui/feedback/Loading';
import { CaseRecordView } from './CaseRecordView';
import { DeleteCaseButton } from '../orders/DeletionControls';
import { useMarkReceived } from '../shipping/useMarkReceived';
import { PurgeCaseButton } from './PurgeCaseButton';
import styles from './CaseRecordScreen.module.css';

/**
 * The case record. Section 5.2.
 *
 * A full screen and not a drawer, deliberately: this is the thing somebody
 * opens when a dentist rings up about a case delivered three months ago, and
 * they need the whole history in front of them rather than a panel over the
 * search they will want to go back to.
 */
export function CaseRecordScreen() {
  const { jo } = useParams<{ jo: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isPending, isError } = useCaseRecord(jo);
  // The question, the mark and the Undo after it, as on the shipping screen and
  // an archive row. The request of 5 October 2026 (third), section 11.
  const received = useMarkReceived();

  if (isPending) return <PageLoading label={strings.common.loadingCase} />;
  if (isError || data === undefined) return <p>{strings.record.notFound}</p>;

  const { jobOrder, kits } = data;
  const firstKit = kits[0];

  const fromOrders = location.pathname.startsWith('/orders/');
  /*
   * A screen that opens the record and is not the archive says where Back goes:
   * Payments does, so a case opened from a payment list goes back to that list
   * and not to an archive it was never in. Request of 5 October 2026.
   */
  const cameFrom = backFrom(location.state);
  /*
   * A deleted case keeps its record, so the Deleted tab can open it and a
   * bookmarked link still works, but nothing is done with it here except
   * restoring it, or deleting it for good, which takes the reader back to the
   * Deleted tab: its trays are off the floor and there is no sheet to print.
   */
  const deleted = jobOrder.deletion.status === 'deleted';
  /*
   * Shipped: it has left the lab and nobody has marked it received. Whoever may
   * mark it does so from here as from its row, which is the only way for
   * Management, who have no shipping screen by the seed.
   */
  const shipped =
    jobOrder.dispatch.dispatched && jobOrder.dispatch.deliveredAt === null && !deleted;

  return (
    <div>
      <button
        className={styles.back}
        onClick={() =>
          void navigate(
            cameFrom?.path ??
              (fromOrders ? '/orders' : deleted ? '/archive?tab=deleted' : '/archive'),
          )
        }
      >
        <GoBackIcon />{' '}
        {cameFrom?.label ?? (fromOrders ? strings.record.backToOrders : strings.record.back)}
      </button>

      {/*
        The whole case, the same component the Final Check reviewer reads.
        Section 4 of the request of 16 September 2026: one extraction and two
        call sites, so the two screens cannot drift apart.
      */}
      <CaseRecordView
        record={data}
        actions={
          <>
            {received.allowed && shipped && (
              <Button
                variant="primary"
                onClick={() =>
                  received.ask({
                    jobOrderNo: jobOrder.jobOrderNo,
                    rowVersion: jobOrder.rowVersion,
                    dispatchedAt: jobOrder.dispatch.dispatchedAt,
                  })
                }
              >
                {strings.shipping.markReceived}
              </Button>
            )}
            {firstKit !== undefined && !deleted && (
              <Button icon={<ArchiveIcon />} onClick={() => void navigate(`/print/${firstKit.id}`)}>
                {strings.record.printSheet}
              </Button>
            )}
            {/* A delivered case is deleted by the same route as a live one. Decision 1.3. */}
            <DeleteCaseButton order={jobOrder} />
            {/* And a deleted one is deleted for good from here as from the Deleted tab. Request of 24 September 2026. */}
            <PurgeCaseButton order={jobOrder} onPurged={() => void navigate('/archive?tab=deleted')} />
          </>
        }
      />

      {received.confirmation}
    </div>
  );
}

/** Where the opening screen asked Back to go, if it asked: `{ backTo: { path, label } }`. */
function backFrom(state: unknown): { path: string; label: string } | null {
  if (typeof state !== 'object' || state === null || !('backTo' in state)) return null;
  const backTo = state.backTo;
  if (typeof backTo !== 'object' || backTo === null) return null;
  const { path, label } = backTo as { path?: unknown; label?: unknown };
  // Only a path inside the app: the state is the history entry's, not ours to trust.
  return typeof path === 'string' &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    typeof label === 'string'
    ? { path, label }
    : null;
}
